import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import AdminTable from '../../../components/AdminTable';
import EmptyRecord from '../../../components/EmptyRecord';
import useFilterMethods from '../../../customHooks/useFilterMethods';
import { GET_PARTNER_REFERRED_USERS_BY_PARTNER } from '../../../graphql/admin_queries';
import DashboardHeaderWithFilters from '../../../components/DashboardHeaderWithFilters';
import EmptyMerchantSettlement from '../../../svgs/EmptyMerchantSettlement';
import useGetQueryParams from '../../../customHooks/useGetQueryParams';
import useTableColumn from '../../../customHooks/useTableColumn';
import useCustomQuery from '../../../network/useCustomQuery';
import useToast from '../../../customHooks/useToast';
import generateCSV from '../../../utils/generateCSV';
import generatePDF from '../../../utils/generatePdf';
import { cleanUpPartnerReferredUsersDetail } from '../../../utils/helpers';

import Styles from './PartnerReferralTableRecord.module.scss';

const limit = 14;
interface IPartnerReferralTableRecord {
  referralId: string;
}

const PartnerReferralTableRecord = ({
  referralId,
}: IPartnerReferralTableRecord) => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const [getPartnerReferredUsers, usersLoading, referredUsers] = useCustomQuery(
    {
      query: GET_PARTNER_REFERRED_USERS_BY_PARTNER,
      onError: () => {},
      onData: () => {},
    },
  );
  const { getQuery, getFilterQueries } = useGetQueryParams();
  const search = getQuery('search') || '';
  const page = Number(getQuery('page'));
  const { push } = useHistory();
  const Toast = useToast();

  const allColumns = [
    ['image', 'email', 'hasBottomValue', 'name'],
    'institutionName',
    'enrollmentType',
    // 'numberOfTenants',
    // 'totalRentPayments',
    'dateOnboarded',
  ];

  const columnLabelFormat = {
    name: 'Name',
    institutionName: 'Institution',
    enrollmentType: 'Enrollment Type',
    // numberOfTenants: 'No. of Tenancies',
    // totalRentPayments: 'Total Rent Payments',
    dateOnboarded: 'Date Onboarded',
  };
  const { getColumns } = useTableColumn(allColumns, 'reward');
  const [usersRec, setUsersRec] = useState(null);

  const setPage = (pageNum) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', pageNum);
    push({
      search: searchParams.toString(),
    });
  };
  if (!page) {
    push(`/rewards/partner-details/${partnerId}?page=1&search=${search}`);
  }

  const onSearchChange = (e) => {
    const result = getFilterQueries();
    push(
      `/rewards/partner-details/${partnerId}?page=1&search=${encodeURIComponent(
        e.target.value,
      )}&fromDateTable=${result?.fromDateTable}&toDateTable=${
        result?.toDateTable
      }`,
    );
  };

  useEffect(() => {
    if (referralId && page) {
      const result = getFilterQueries();
      getPartnerReferredUsers({
        variables: {
          offset: (page - 1) * limit,
          limit,
          partnerId: referralId,
          search,
          from: result.fromDateTable,
          to: result.toDateTable,
        },
      });
    }
  }, [page, search, referralId]);

  const getPartnerReferredUsersFunc = (result) => {
    getPartnerReferredUsers({
      variables: {
        offset: 0,
        limit,
        partnerId: referralId,
        search,
        from: result?.fromDateTable,
        to: result?.toDateTable,
      },
    });
  };

  const onFilterSubmit = (result) => {
    if (referralId) {
      getPartnerReferredUsersFunc(result);
    }
  };

  const { filterObj } = useFilterMethods({
    allowedFilters: ['tableDateRange'],
    defaultUrl: `/rewards/partner-details/${partnerId}?page=1`,
    cb: onFilterSubmit,
    Toast,
  });

  useEffect(() => {
    if (referredUsers?.getPartnerReferredUsersByPartnerId?.data) {
      setUsersRec(referredUsers?.getPartnerReferredUsersByPartnerId?.data);
    }
  }, [referredUsers]);

  const [getExportRecord] = useCustomQuery({
    query: GET_PARTNER_REFERRED_USERS_BY_PARTNER,
    onError: () => {},
    onData: () => {},
  });

  const handleExport = async (exportType: string) => {
    try {
      const result = getFilterQueries();
      const output = await getExportRecord({
        variables: {
          offset: 0,
          limit: 2000,
          partnerId: referralId,
          search,
          from: result?.fromDateTable,
          to: result?.toDateTable,
        },
      });

      if (output?.data?.getPartnerReferredUsersByPartnerId?.data) {
        const rec = cleanUpPartnerReferredUsersDetail(
          output?.data?.getPartnerReferredUsersByPartnerId?.data?.users,
        );
        const func = exportType === 'pdf' ? generatePDF : generateCSV;
        const selectedHeaders = [
          'S/N',
          ...(getColumns?.()?.map((column) => (column === 'image__email__hasBottomValue__name'
            ? ['name', 'email']
            : column)) || []),
        ];

        func({
          payload: rec,
          title: 'Partner Referred Users',
          selectedHeaders,
          formatHeaders: columnLabelFormat,
          cb: () => Toast.success('Exported successfully'),
        });
      }
    } catch (error) {
      Toast.error('Error while exporting');
    }
  };

  return (
    <div className={Styles.wrapper}>
      <div className={Styles.wrapper__header}>
        <DashboardHeaderWithFilters
          title="Referral statistics"
          filterObj={filterObj}
          dateType="tableDateRange"
          showSearch
          search={search}
          onSearchChange={onSearchChange}
          searchPlaceholder="Customer name /  Email address"
          exportObj={{
            handleExport,
          }}
        />
      </div>

      <div className="transaction-table-details-cover">
        {usersRec?.count ? (
          <AdminTable
            refreshing={usersLoading}
            page={page}
            setPage={setPage}
            pageSize={limit}
            minHeight="calc(100vh - 430px)"
            count={usersRec?.count}
            contents={cleanUpPartnerReferredUsersDetail(usersRec?.users)}
            reference="id"
            hideDominantImage
            hasNameAndImage
            clickable={false}
            allowKeyArr={getColumns(true)}
            mobileAllowKeyArr={[
              ['name', 'email'],
              ['institutionName', 'enrollmentType'],
              ['numberOfTenants', 'totalRentPayments'],
            ]}
            formatLabels={columnLabelFormat}
          />
        ) : (
          <EmptyRecord
            height="calc(100vh - 380px)"
            title={
              usersLoading
                ? 'Fetching referred users...'
                : 'No referred users yet'
            }
            main="All users referred by this partner will appear here"
            loading={usersLoading}
            Icon={EmptyMerchantSettlement}
          />
        )}
      </div>
    </div>
  );
};

export default PartnerReferralTableRecord;
